// Support inbox (PX-218) — paginated ticket list with status / platform
// filters and email/message search. Row click opens the detail page where
// status, notes, compensation and push reply live.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { request } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { hasAnyPerm } from '@/lib/perms'
import { PERM } from '@/types/api'
import type {
  AdminSupportStats,
  AdminSupportTicketDetail,
  AdminSupportTicketListItem,
  AdminSupportTicketListResponse,
  AdminSupportTicketUpdate,
  SupportTicketPlatform,
  SupportTicketStatus,
} from '@/types/api'

const STATUS_FILTERS: { value: SupportTicketStatus | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'answered', label: 'Answered' },
  { value: 'closed', label: 'Closed' },
]

// PX-436: the 4 real statuses (no 'All') for the inline list-row status select.
const SUPPORT_STATUSES: SupportTicketStatus[] = ['new', 'in_progress', 'answered', 'closed']

export const SUPPORT_STATUS_TONE: Record<SupportTicketStatus, string> = {
  new: 'bg-brand-50 text-brand-700',
  in_progress: 'bg-amber-100 text-amber-800',
  answered: 'bg-emerald-100 text-emerald-800',
  closed: 'bg-ink-100 text-ink-500',
}

export function SupportStatusPill({ status }: { status: SupportTicketStatus }) {
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${SUPPORT_STATUS_TONE[status]}`}>
      {status.replace('_', ' ')}
    </span>
  )
}

export function SupportPage() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const canWrite = hasAnyPerm(profile, [PERM.support_write])
  const [status, setStatus] = useState<SupportTicketStatus | ''>('')
  const [platform, setPlatform] = useState<SupportTicketPlatform | ''>('')
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [cursor, setCursor] = useState<string | null>(null)

  const statsQuery = useQuery({
    queryKey: ['admin', 'support', 'stats'],
    queryFn: () => request<AdminSupportStats>('/admin/support/stats'),
  })

  const listQuery = useQuery({
    queryKey: ['admin', 'support', { status, platform, appliedSearch, cursor }],
    queryFn: () =>
      request<AdminSupportTicketListResponse>('/admin/support', {
        query: {
          status: status || undefined,
          platform: platform || undefined,
          search: appliedSearch || undefined,
          cursor: cursor ?? undefined,
        },
      }),
  })

  const stats = statsQuery.data

  return (
    <div className="p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Support</h1>
        <p className="text-sm text-ink-400">
          User tickets from the in-app form and pinxieai.com/support.
          {stats && ` ${stats.new} new · ${stats.in_progress} in progress · ${stats.answered} answered · ${stats.closed} closed.`}
        </p>
      </header>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex gap-1 rounded-lg bg-ink-100 p-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              type="button"
              onClick={() => { setStatus(f.value); setCursor(null) }}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                status === f.value ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900'
              }`}
            >
              {f.label}
              {f.value === 'new' && stats && stats.new > 0 && (
                <span className="ml-1.5 rounded-full bg-brand-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  {stats.new}
                </span>
              )}
            </button>
          ))}
        </div>
        <select
          value={platform}
          onChange={(e) => { setPlatform(e.target.value as SupportTicketPlatform | ''); setCursor(null) }}
          className="rounded-md border border-ink-200 bg-white px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
        >
          <option value="">All platforms</option>
          <option value="ios">iOS</option>
          <option value="web">Web</option>
        </select>
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => { e.preventDefault(); setAppliedSearch(search.trim()); setCursor(null) }}
        >
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email or message…"
            className="w-64 rounded-md border border-ink-200 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
          <button
            type="submit"
            className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:border-ink-300"
          >
            Search
          </button>
        </form>
      </div>

      <div className="overflow-x-auto rounded-xl border border-ink-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-400">
            <tr>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">From</th>
              <th className="px-4 py-3">Message</th>
              <th className="px-4 py-3">Platform</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Linear</th>
            </tr>
          </thead>
          <tbody>
            {listQuery.isLoading && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-400">Loading…</td></tr>
            )}
            {listQuery.data?.items.map((t) => (
              <TicketRow key={t.id} ticket={t} canWrite={canWrite} onOpen={() => navigate(`/support/${t.id}`)} />
            ))}
            {!listQuery.isLoading && listQuery.data?.items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-400">No tickets match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {listQuery.data?.has_more && listQuery.data.cursor && (
        <div className="mt-4 text-right">
          <button
            type="button"
            onClick={() => setCursor(listQuery.data!.cursor!)}
            className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:border-ink-300"
          >
            Next page →
          </button>
        </div>
      )}
    </div>
  )
}

function TicketRow({ ticket, canWrite, onOpen }: { ticket: AdminSupportTicketListItem; canWrite: boolean; onOpen: () => void }) {
  return (
    <tr
      onClick={onOpen}
      className="cursor-pointer border-b border-ink-100 align-top transition last:border-0 hover:bg-ink-50"
    >
      <td className="px-4 py-3 text-xs text-ink-500">{new Date(ticket.created_at).toLocaleString()}</td>
      <td className="px-4 py-3">
        <div className="text-ink-700">{ticket.email ?? 'no email'}</div>
        {ticket.user_id ? (
          <div className="font-mono text-[10px] text-ink-400">{ticket.user_id}</div>
        ) : (
          <div className="text-[10px] text-ink-400">anonymous</div>
        )}
      </td>
      <td className="max-w-md px-4 py-3">
        <div className="line-clamp-2 text-ink-700" title={ticket.message}>{ticket.message}</div>
        {ticket.attachment_urls.length > 0 && (
          <div className="mt-1 text-xs text-ink-400">📎 {ticket.attachment_urls.length} attachment{ticket.attachment_urls.length > 1 ? 's' : ''}</div>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-ink-500">
        {ticket.platform}
        {ticket.app_version && <span className="ml-1 font-mono text-[10px] text-ink-400">v{ticket.app_version}</span>}
      </td>
      <td className="px-4 py-3">
        {canWrite ? <InlineStatusSelect ticket={ticket} /> : <SupportStatusPill status={ticket.status} />}
      </td>
      <td className="px-4 py-3 text-xs">
        {ticket.linear_issue_url ? (
          <a
            href={ticket.linear_issue_url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-brand-700 hover:underline"
          >
            issue ↗
          </a>
        ) : (
          <span className="text-ink-300">—</span>
        )}
      </td>
    </tr>
  )
}

// PX-436: inline status change straight from the list row — PATCH /admin/support/:id
// without opening the ticket. Gated by support_write at the call site. Stops click
// propagation so changing status doesn't also navigate into the ticket.
function InlineStatusSelect({ ticket }: { ticket: AdminSupportTicketListItem }) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (status: SupportTicketStatus) =>
      request<AdminSupportTicketDetail>(`/admin/support/${ticket.id}`, {
        method: 'PATCH',
        body: { status } satisfies AdminSupportTicketUpdate,
      }),
    // Prefix-invalidate so the list AND the stats counts (New badge) refresh.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'support'] }),
  })
  return (
    <select
      value={ticket.status}
      disabled={mutation.isPending}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation()
        mutation.mutate(e.target.value as SupportTicketStatus)
      }}
      className={`cursor-pointer rounded border-0 px-2 py-0.5 text-xs font-medium capitalize focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:opacity-50 ${SUPPORT_STATUS_TONE[ticket.status]}`}
    >
      {SUPPORT_STATUSES.map((s) => (
        <option key={s} value={s}>{s.replace('_', ' ')}</option>
      ))}
    </select>
  )
}
